import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './FloorPlanSettingsPopup.css';
import {
  selectSortedFloorPlans,
  selectTotalRealFloorCount,
  updateFloorPlanAcrossTabs,
  FLOOR_TYPES,
  EDITABLE_FLOOR_TYPES,
  getRepresentedFloors,
} from '../../../redux/floorPlanSlice';

const FLOOR_TYPE_LABELS = {
  [FLOOR_TYPES.GROUND]: 'Zemin',
  [FLOOR_TYPES.BASEMENT]: 'Bodrum',
  [FLOOR_TYPES.MEZZANINE]: 'Asma',
  [FLOOR_TYPES.GALLERY]: 'Galeri',
  [FLOOR_TYPES.NORMAL]: 'Normal',
  [FLOOR_TYPES.ROOF]: 'Çatı',
};

const FloorPlanSettingsPopup = ({ isOpen, onClose }) => {
  const popupRef = useRef(null);
  const dispatch = useDispatch();
  const sortedPlans = useSelector(selectSortedFloorPlans);
  const totalRealFloorCount = useSelector(selectTotalRealFloorCount);

  // ESC key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleFloorCountChange = (planId, newCount) => {
    // Tüm tab'lardaki linked plan'lara yay (mimari + elektrik + telekom + yangin).
    // Sadece aktif tab güncellenirse YC/Kolon Şeması elektrik tab'ından okuyup
    // floorCount=1 görüyor → kat çoğaltmayı gerçekleştiremiyor.
    dispatch(updateFloorPlanAcrossTabs({
      id: planId,
      changes: { floorCount: newCount },
    }));
  };

  const isEditable = (type) => !!EDITABLE_FLOOR_TYPES[type];

  const getRange = (type) => EDITABLE_FLOOR_TYPES[type] || { min: 1, max: 1 };

  return (
    <div className="floor-settings-popup-overlay">
      <div className="floor-settings-popup" ref={popupRef}>
        {/* Header */}
        <div className="floor-settings-popup-header">
          <h3>Kat Plan&#305; Ayarlar&#305;</h3>
          <button className="close-button" onClick={onClose}>
            &#10005;
          </button>
        </div>

        {/* Content */}
        <div className="floor-settings-popup-content">
          {sortedPlans.map((plan) => {
            const editable = isEditable(plan.type);
            const range = getRange(plan.type);
            const floorCount = plan.floorCount || 1;
            const representedFloors = getRepresentedFloors(plan);

            return (
              <div key={plan.id} className="floor-settings-row">
                <div className="floor-settings-row-main">
                  <span className="floor-settings-name">{plan.name}</span>
                  <span className={`floor-type-badge ${plan.type}`}>
                    {FLOOR_TYPE_LABELS[plan.type] || plan.type}
                  </span>

                  {editable ? (
                    <div className="floor-count-stepper">
                      <button
                        onClick={() => handleFloorCountChange(plan.id, floorCount - 1)}
                        disabled={floorCount <= range.min}
                      >
                        &#9660;
                      </button>
                      <span className="floor-count-value">{floorCount}</span>
                      <button
                        onClick={() => handleFloorCountChange(plan.id, floorCount + 1)}
                        disabled={floorCount >= range.max}
                      >
                        &#9650;
                      </button>
                    </div>
                  ) : (
                    <div className="floor-count-locked">
                      <span className="floor-count-value" style={{ borderRadius: 4, border: '1px solid #555' }}>
                        {floorCount}
                      </span>
                      <span className="lock-icon">&#128274;</span>
                    </div>
                  )}
                </div>

                {/* Preview: temsil edilen kat isimleri */}
                {editable && floorCount > 1 && (
                  <div className="floor-settings-preview">
                    &rarr; {representedFloors.join(', ')}
                  </div>
                )}
              </div>
            );
          })}

          {sortedPlans.length === 0 && (
            <div style={{ color: '#888', textAlign: 'center', padding: '20px 0' }}>
              Hen&uuml;z kat plan&#305; eklenmedi.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="floor-settings-popup-footer">
          <span className="floor-settings-total">
            Toplam Ger&ccedil;ek Kat: {totalRealFloorCount}
          </span>
          <button className="confirm-button" onClick={onClose}>
            Tamam
          </button>
        </div>
      </div>
    </div>
  );
};

export default FloorPlanSettingsPopup;
